import React, { useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { Tab, Tabs, Theme } from "@mui/material";
import { makeStyles, withStyles } from "tss-react/mui";

import SearchNotFound from "@/components/SearchNotFound";
import { graphql } from "@/gql";
import useSearchText from "@/hooks/useSearchText";
import { colors } from "@/theme/overrides/colors";
import { formatISODate } from "@/utils/datetime";

import ClientList from "./components/ClientList";
import ClientSearchResult from "./components/ClientSearchResult";
import CLientsLoading from "./components/ClientsLoading";
import SearchPanel from "./components/SearchPanel";
import TabPanel from "./components/TabPanel";

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(4, 8),
  },

  linkContainer: {
    background: colors.white,
    padding: theme.spacing(4, 6),
  },

  tabPanelContainer: {
    padding: theme.spacing(4, 6),
  },
}));

const StyledTabs = withStyles(Tabs, {
  indicator: {
    backgroundColor: colors.primary,
  },
});

const clientListPageQuery = graphql(`
  query ClientListPage($currentDate: ISO8601Date!) {
    activeClients {
      nodes {
        ...ClientCard
      }
    }
    strugglingClients {
      nodes {
        ...ClientCard
      }
    }
  }
`);

const ClientListPage: React.FC = () => {
  const { classes } = useStyles();
  const [value, setValue] = React.useState(0);
  const { loading, data } = useQuery(clientListPageQuery, {
    variables: { currentDate: formatISODate(new Date()) },
  });

  //search states
  const [searchText, setSearchText] = useSearchText({ debounce: 500 });

  const handleChange = (
    event: React.ChangeEvent<EventTarget | Element>,
    newValue: number
  ) => {
    setValue(newValue);
  };

  const activeClients = useMemo(() => {
    return (
      data?.activeClients.nodes.filter((client) =>
        client.profile?.fullName
          ?.toLowerCase()
          .includes(searchText.toLowerCase())
      ) ?? []
    );
  }, [data?.activeClients, searchText]);

  const strugglingClients = useMemo(() => {
    return (
      data?.strugglingClients.nodes.filter((client) =>
        client.profile?.fullName
          ?.toLowerCase()
          .includes(searchText.toLowerCase())
      ) ?? []
    );
  }, [data?.strugglingClients, searchText]);

  return (
    <div className={classes.root}>
      <div className={classes.linkContainer}>
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label="simple tabs example"
        >
          <Tab label={"Active Clients"} {...a11yProps(0)} />
          <Tab label={"Struggling Clients"} {...a11yProps(1)} />
        </StyledTabs>
      </div>

      <SearchPanel onChange={(e) => setSearchText(e.target.value)} />

      {loading ? (
        <>
          <CLientsLoading />
        </>
      ) : (
        <>
          <TabPanel value={value} index={0}>
            <ClientList clients={activeClients} />
            {searchText != "" && activeClients.length === 0 && (
              <SearchNotFound searchQuery={searchText} />
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <ClientList clients={strugglingClients} />
            {searchText != "" && strugglingClients.length === 0 && (
              <SearchNotFound searchQuery={searchText} />
            )}
          </TabPanel>
        </>
      )}
    </div>
  );
};

export default ClientListPage;
